import { Check } from 'lucide-react';

import { Badge, EmptyState, Modal } from '~/components/ui';
import type {
  PlacementComponentOption,
  PlacementComponentVersionOption,
} from '~/modules/query/ai/types';

interface ComponentPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  components: PlacementComponentOption[];
  componentId: string;
  componentVersion: string;
  onSelect: (componentId: string, version: PlacementComponentVersionOption) => void;
}

const summarise = (value: Record<string, unknown>): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return '—';
  }
};

/** The static component catalogue, one card per component and one row per version. */
export default function ComponentPickerModal({
  isOpen,
  onClose,
  components,
  componentId,
  componentVersion,
  onSelect,
}: ComponentPickerModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title="Choose a component"
      description="The component is where this placement's AI output appears. Picking a version also resets its sample test data."
      testId="ai-placement-component-picker"
    >
      {components.length === 0 ? (
        <EmptyState
          title="No components are available"
          description="The placement catalogue is empty, so a placement cannot be created yet."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {components.map((component) => (
            <div
              key={component.id}
              className="rounded-lg border border-gray-200 dark:border-gray-800"
            >
              <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {component.label}
                </p>
                <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{component.id}</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {component.description}
                </p>
              </div>
              <div className="space-y-2 p-3">
                {component.versions.map((version) => {
                  const selected =
                    component.id === componentId && version.version === componentVersion;

                  return (
                    <button
                      key={version.version}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        onSelect(component.id, version);
                        onClose();
                      }}
                      data-testid={`ai-placement-component-${component.id}-${version.version}`}
                      className={`w-full rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                        selected
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                          : 'border-gray-200 hover:border-brand-300 dark:border-gray-800 dark:hover:border-brand-500/60'
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-800 dark:text-gray-200">
                            v{version.version}
                          </span>
                          <Badge color={version.status === 'active' ? 'success' : 'gray'} size="sm">
                            {version.status === 'active' ? 'Active' : 'Deprecated'}
                          </Badge>
                        </span>
                        {selected ? (
                          <Check
                            className="size-4 text-brand-500 dark:text-brand-400"
                            aria-hidden="true"
                          />
                        ) : null}
                      </span>
                      <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                        {version.releaseNote}
                      </span>
                      <span
                        className="mt-2 block truncate font-mono text-xs text-gray-500 dark:text-gray-400"
                        title={summarise(version.defaultContext)}
                      >
                        Context {summarise(version.defaultContext)}
                      </span>
                      <span
                        className="mt-1 block truncate font-mono text-xs text-gray-500 dark:text-gray-400"
                        title={summarise(version.defaultProps)}
                      >
                        Props {summarise(version.defaultProps)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
